import React, { FC, ReactNode, useMemo } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';

import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { isRTL } from '@webtrader/core/i18n';
import { useTheme } from '@webtrader/core/services/theme/v2';

import MaterialIcons from '@/components/Icons/Material';
import Text from '@/components/Text';
import TouchableOpacity from '@/components/Touch/Opacity';

interface IProps {
  title?: ReactNode;
  suffix?: ReactNode;
  description?: ReactNode;
  onClose?: () => void;
}

const ModalHeader: FC<IProps> = (props) => {
  const { title, suffix, description, onClose } = props;

  const { theme } = useTheme();

  const headerTitle = useMemo(() => {
    if (typeof title === 'string') {
      return <HeaderTitleText>{title}</HeaderTitleText>;
    }

    return title;
  }, [title]);

  const headerDescription = useMemo(() => {
    if (typeof description === 'string') {
      return <HeaderTitleDescriptionText>{description}</HeaderTitleDescriptionText>;
    }

    return description;
  }, [description]);

  return (
    <Header dir={isRTL() ? 'rtl' : 'ltr'}>
      {onClose && (
        <HeaderClose onPress={onClose} testID={EDataTestId.MODAL_CLOSE_BUTTON}>
          <MaterialIcons name="close" size={24} color={theme.colors.text[0]} />
        </HeaderClose>
      )}
      <HeaderTitle testID={EDataTestId.MODAL_TITLE}>
        {headerTitle}
        {description && headerDescription}
      </HeaderTitle>
      {suffix && <HeaderSuffix>{suffix}</HeaderSuffix>}
    </Header>
  );
};

export default ModalHeader;

const Header = styled(View)`
  height: ${dynamicSize.convertSize(46)}px;
  padding-top: ${dynamicSize.convertSize(10)}px;
  padding-start: ${dynamicSize.convertSize(10)}px;
  padding-end: ${dynamicSize.convertSize(10)}px;
  padding-bottom: ${dynamicSize.convertSize(4)}px;

  flex-direction: row;

  align-items: center;
  justify-content: space-between;
`;

const HeaderClose = styled(TouchableOpacity)``;

const HeaderTitle = styled.View`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const HeaderTitleText = styled(Text)`
  font-size: ${dynamicSize.convertSize(16)}px;
  font-weight: 500;
`;

const HeaderTitleDescriptionText = styled(Text)`
  color: ${({ theme }) => theme.colors.text[2]};
  font-size: ${dynamicSize.convertSize(13)}px;
`;

const HeaderSuffix = styled.View`
  flex-direction: row;
  justify-content: flex-end;
`;
